import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'divinea-theme';
  private readonly THEME_CLASS = 'dark-mode';
  
  private currentThemeSubject = new BehaviorSubject<Theme>('light');
  public currentTheme$: Observable<Theme> = this.currentThemeSubject.asObservable();

  constructor() {
    this.initializeTheme();
  }

  /**
   * Initialize theme from localStorage or system preference
   */
  private initializeTheme(): void {
    const savedTheme = this.getStoredTheme();
    
    let theme: Theme = savedTheme || 'light';
    
    this.setTheme(theme);
  }

  /**
   * Get stored theme from localStorage
   */
  private getStoredTheme(): Theme | null {
    try {
      const stored = localStorage.getItem(this.THEME_KEY);
      return stored as Theme || null;
    } catch {
      return null;
    }
  }

  /**
   * Check if system prefers dark mode
   */
  private getSystemPreference(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Set the current theme
   */
  public setTheme(theme: Theme): void {
    this.currentThemeSubject.next(theme);
    this.applyTheme(theme);
    this.storeTheme(theme);
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: Theme): void {
    const root = document.documentElement;
    const isDark = this.shouldUseDarkMode(theme);
    
    if (isDark) {
      root.classList.add(this.THEME_CLASS);
      root.classList.remove('light-mode');
    } else {
      root.classList.remove(this.THEME_CLASS);
      root.classList.add('light-mode');
    }
  }

  /**
   * Determine if dark mode should be used
   */
  private shouldUseDarkMode(theme: Theme): boolean {
    switch (theme) {
      case 'dark':
        return true;
      case 'light':
        return false;
      case 'system':
        return this.getSystemPreference();
      default:
        return false;
    }
  }

  /**
   * Store theme in localStorage
   */
  private storeTheme(theme: Theme): void {
    try {
      localStorage.setItem(this.THEME_KEY, theme);
    } catch (error) {
      console.warn('Failed to store theme preference:', error);
    }
  }

  /**
   * Toggle between light and dark mode
   */
  public toggleTheme(): void {
    const currentTheme = this.currentThemeSubject.value;
    const newTheme: Theme = currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
  }

  /**
   * Get current theme
   */
  public getCurrentTheme(): Theme {
    return this.currentThemeSubject.value;
  }

  /**
   * Check if dark mode is currently active
   */
  public isDarkMode(): boolean {
    return this.shouldUseDarkMode(this.currentThemeSubject.value);
  }

  /**
   * Listen for system theme changes
   */
  public listenForSystemChanges(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    mediaQuery.addEventListener('change', (e) => {
      const currentTheme = this.currentThemeSubject.value;
      if (currentTheme === 'system') {
        this.applyTheme('system');
      }
    });
  }
}
